import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { HERO_SLIDES } from "@/mock";

const INTERVAL = 3500;

/**
 * HeroCarousel - full-bleed lifestyle images with large overlaid offer text.
 * Crossfades, auto-advances, pauses on hover/focus, prev/next + dots + keyboard.
 */
export default function HeroCarousel({ slides = HERO_SLIDES }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = slides.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (paused || count <= 1) return undefined;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % count), INTERVAL);
    return () => clearTimeout(timer.current);
  }, [index, paused, count]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Current offers"
      className="relative w-full select-none bg-neutral-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div className="relative aspect-[1000/405] overflow-hidden">
        {slides.map((s, i) => (
          <div
            key={s.image}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            aria-hidden={i !== index}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            {s.preRendered ? (
              <Link
                to={s.to}
                tabIndex={i === index ? 0 : -1}
                aria-label={s.alt}
                className="flex h-full w-full items-center justify-center"
                style={{ backgroundColor: s.bg || "#000" }}
              >
                <picture className="h-full w-full">
                  {s.image.startsWith("/banners/") && (
                    <source
                      media="(max-width: 640px)"
                      srcSet={s.image.replace("/banners/", "/banners/mobile/")}
                      type="image/webp"
                    />
                  )}
                  <img
                    src={s.image}
                    alt={s.alt}
                    fetchPriority={i === 0 ? "high" : "auto"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-contain"
                  />
                </picture>
              </Link>
            ) : (
              <>
                <img
                  src={s.image}
                  alt={s.headline}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)",
                  }}
                  aria-hidden="true"
                />
                <div className="relative mx-auto flex h-full max-w-7xl items-center px-6 sm:px-10 lg:px-16">
                  <div className="max-w-xl text-white">
                    {s.eyebrow && (
                      <span className="inline-block rounded-full bg-[#D0021B] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white">
                        {s.eyebrow}
                      </span>
                    )}
                    <h2 className="mt-4 text-3xl font-black uppercase leading-[1.05] drop-shadow-md sm:text-5xl lg:text-6xl">
                      {s.headline}
                    </h2>
                    {s.sub && (
                      <p className="mt-4 max-w-md text-sm text-white/90 drop-shadow sm:text-lg">{s.sub}</p>
                    )}
                    <Link
                      to={s.to}
                      tabIndex={i === index ? 0 : -1}
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016] sm:mt-7"
                    >
                      {s.cta || "Shop Now"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Prev / Next */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous slide"
        data-testid="hero-prev"
        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-neutral-900 shadow-md transition hover:bg-white"
      >
        <ChevronLeft className="h-6 w-6" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next slide"
        data-testid="hero-next"
        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-neutral-900 shadow-md transition hover:bg-white"
      >
        <ChevronRight className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.image}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            aria-current={i === index}
            className={`h-2.5 rounded-full transition-all ${
              i === index ? "w-6 bg-white" : "w-2.5 bg-white/60 hover:bg-white/90"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
